import { Command } from 'cmdk'
import { useNavigate } from 'react-router-dom'
import { useUIStore } from '@/stores/ui'
import { useQuery } from '@tanstack/react-query'
import { get } from '@/lib/api'
import { useState } from 'react'
import { Plane, Users, Inbox, BookOpen, Building2, TrendingUp, BarChart3, Settings, PlusSquare, LifeBuoy } from 'lucide-react'

interface SearchHit { id: string; type: 'contacts' | 'accounts' | 'cases'; title: string; subtitle: string | null }

export function CommandPalette() {
  const { commandPaletteOpen: open, closeCommandPalette: close } = useUIStore()
  const [value, setValue] = useState('')
  const navigate = useNavigate()

  const { data, isFetching } = useQuery({
    queryKey: ['global-search', value],
    queryFn: () => get<{ contacts: SearchHit[]; accounts: SearchHit[]; cases: SearchHit[] }>(
      '/api/v1/search',
      { query: { q: value, types: 'contacts,accounts,cases' } },
    ),
    enabled: open && value.length >= 2,
    staleTime: 10_000,
  })

  function go(path: string) {
    close()
    navigate(path)
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center pt-[10vh] px-4">
      <div className="absolute inset-0 bg-heading/40" onClick={close} aria-hidden="true" />
      <Command
        label="Command palette"
        className="relative bg-card rounded-card shadow-elevated w-full max-w-xl overflow-hidden border border-border"
      >
        <Command.Input
          placeholder="Search cases, contacts, accounts, or type a command…"
          value={value}
          onValueChange={setValue}
          className="w-full h-12 px-4 text-sm outline-none bg-card border-b border-border placeholder:text-muted"
        />
        <Command.List className="max-h-[60vh] overflow-auto p-2">
          <Command.Empty className="px-3 py-4 text-sm text-muted">
            {isFetching ? 'Searching…' : 'Type at least 2 characters to search.'}
          </Command.Empty>

          <Command.Group heading="Actions" className="text-[11px] font-semibold text-muted uppercase tracking-wider px-2 py-1">
            <Item icon={<PlusSquare className="h-4 w-4" />} label="New case" onSelect={() => go('/cases?new=1')} />
            <Item icon={<PlusSquare className="h-4 w-4" />} label="New contact" onSelect={() => go('/contacts?new=1')} />
            <Item icon={<PlusSquare className="h-4 w-4" />} label="New lead" onSelect={() => go('/leads?new=1')} />
          </Command.Group>

          <Command.Group heading="Navigation" className="text-[11px] font-semibold text-muted uppercase tracking-wider px-2 py-1 mt-2">
            <Item icon={<BarChart3 className="h-4 w-4" />} label="Dashboard" onSelect={() => go('/')} />
            <Item icon={<Inbox className="h-4 w-4" />} label="Cases" onSelect={() => go('/cases')} />
            <Item icon={<Users className="h-4 w-4" />} label="Contacts" onSelect={() => go('/contacts')} />
            <Item icon={<Building2 className="h-4 w-4" />} label="Accounts" onSelect={() => go('/accounts')} />
            <Item icon={<TrendingUp className="h-4 w-4" />} label="Opportunities" onSelect={() => go('/opportunities')} />
            <Item icon={<BookOpen className="h-4 w-4" />} label="Knowledge Base" onSelect={() => go('/kb')} />
            <Item icon={<Settings className="h-4 w-4" />} label="Settings" onSelect={() => go('/settings')} />
          </Command.Group>

          {data && (data.cases?.length ?? 0) > 0 ? (
            <Command.Group heading="Cases" className="text-[11px] font-semibold text-muted uppercase tracking-wider px-2 py-1 mt-2">
              {data.cases!.map((hit) => (
                <Item
                  key={`case-${hit.id}`}
                  icon={<Inbox className="h-4 w-4 text-violet" />}
                  label={hit.title}
                  hint={hit.subtitle ?? undefined}
                  onSelect={() => go(`/cases/${hit.id}`)}
                />
              ))}
            </Command.Group>
          ) : null}
          {data && (data.contacts?.length ?? 0) > 0 ? (
            <Command.Group heading="Contacts" className="text-[11px] font-semibold text-muted uppercase tracking-wider px-2 py-1 mt-2">
              {data.contacts!.map((hit) => (
                <Item
                  key={`contact-${hit.id}`}
                  icon={<Users className="h-4 w-4 text-violet" />}
                  label={hit.title}
                  hint={hit.subtitle ?? undefined}
                  onSelect={() => go(`/contacts/${hit.id}`)}
                />
              ))}
            </Command.Group>
          ) : null}
          {data && (data.accounts?.length ?? 0) > 0 ? (
            <Command.Group heading="Accounts" className="text-[11px] font-semibold text-muted uppercase tracking-wider px-2 py-1 mt-2">
              {data.accounts!.map((hit) => (
                <Item
                  key={`acct-${hit.id}`}
                  icon={<Building2 className="h-4 w-4 text-violet" />}
                  label={hit.title}
                  hint={hit.subtitle ?? undefined}
                  onSelect={() => go(`/accounts/${hit.id}`)}
                />
              ))}
            </Command.Group>
          ) : null}
        </Command.List>
        <div className="px-3 py-2 border-t border-border text-[11px] text-muted flex items-center gap-4">
          <span><kbd className="font-sans">↑↓</kbd> navigate</span>
          <span><kbd className="font-sans">↵</kbd> open</span>
          <span><kbd className="font-sans">Esc</kbd> close</span>
          <LifeBuoy className="h-3 w-3 ml-auto" />
        </div>
      </Command>
    </div>
  )
}

function Item({
  icon, label, hint, onSelect,
}: { icon: React.ReactNode; label: string; hint?: string; onSelect: () => void }) {
  return (
    <Command.Item
      onSelect={onSelect}
      className="flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer aria-selected:bg-violet-tint text-sm"
    >
      {icon}
      <div className="flex-1">
        <div className="text-body">{label}</div>
        {hint ? <div className="text-xs text-muted">{hint}</div> : null}
      </div>
    </Command.Item>
  )
}
